
import { Vue, Options, prop } from "vue-class-component";
import { createFocusTrap } from "focus-trap";
import {
  ellipsisString,
  formatByCountry,
  formatDateNormal,
  formatPriceRP
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { PrintReceiptController } from "@/app/ui/controllers/PrintReceiptController";
import { PaymentController } from "@/app/ui/controllers/PaymentController";
import { FlagsPermissionBooking } from "@/feature-flags/flag-booking";
import { SuccessBookingV4 } from "@/domain/entities/BookingV4";
import { DetailDiscount, STT } from "@/domain/entities/CreateBookingV3";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { convertSnakeToCamel } from "@/app/infrastructures/misc/common-library/ConvertSnakeToCamel";
import { CreateBookingV4, PaymentMethodV4 } from "@/domain/entities/BookingV4";
import { ClientBranchSelectionV4 } from "@/domain/entities/BookingV4Client";
import formatInputMoneyWithDecimal from "@/app/infrastructures/misc/common-library/FormatInputMoneyWithDecimal";

class Props {
  modelValue = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  data = prop<SuccessBookingV4>({ required: true });
  receiptFormat = prop<string>({ default: "a4" }); // "a6" | "a4" | "none"
  isButtonCreateInvoice = prop<boolean>({ default: true, type: Boolean });
  totalPieces = prop<number>({ default: 1, required: true });
  currency = prop<string>({ default: "" });
  bookingClient = prop<boolean>({ default: false, type: Boolean });
  selectedClient = prop<ClientBranchSelectionV4>({ default: null });
  isCrossCountryCod = prop<boolean>({ default: false, type: Boolean });
  prefixCurrency = prop<string>({ required: true, type: String });
}

@Options({
  emits: ["print", "close", "previewInvoice"]
})
class ModalSuccessBookingV4 extends Vue.with(Props) {
  focusTrap: any = null;
  messageEntities = new ModalMessageEntities();

  async mounted() {
    this.setBookingSuccessModal();
    await this.$nextTick();
    this.focusTrap = createFocusTrap((this.$refs["modal-ref"] as any).$el, {
      escapeDeactivates: true,
      allowOutsideClick: true,
      returnFocusOnDeactivate: true,
      clickOutsideDeactivates: false,
      onDeactivate: () => {
        this.$emit("close");
      }
    });
    this.focusTrap.activate();
  }

  unmounted() {
    this.deactivateFocusTrap();
  }

  get detailData() {
    return new SuccessBookingV4(convertSnakeToCamel(this.data));
  }

  get detailStt() {
    return (
      this.data.stt.map((item: any) => {
        return new STT({
          ...convertSnakeToCamel(item),
          listDiscount: Array.isArray(item.list_discount)
            ? item.list_discount.map((discount: any) => {
                return new DetailDiscount({
                  ...convertSnakeToCamel(discount)
                });
              })
            : []
        });
      })[0] || new STT()
    );
  }

  get isAccountForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get bookingClientCountryCode(): string {
    if (AccountController.accountData.isAllClient) {
      return AccountController.accountData.countryCode || "id";
    }
    return (
      this.selectedClient?.clientCountryCode ||
      this.selectedClient?.clientCountry ||
      this.selectedClient?.clientPartner?.partnerLocation?.country?.code ||
      "id"
    ).toLowerCase();
  }

  get isClientForeign(): boolean {
    return this.bookingClientCountryCode === "my";
  }

  get currencyCode(): "IDR" | "MYR" {
    if (this.currency === "IDR" || this.currency === "MYR") {
      return this.currency;
    }
    return this.isAccountForeign ? "MYR" : "IDR";
  }

  get printButtonTitle(): string {
    const baseTitle = this.$t("bookingV4.success.printReceipt") as string;
    const formatUpper = (this.receiptFormat || "").toUpperCase();

    if (formatUpper === "A6" || formatUpper === "A4") {
      return `${baseTitle} ${formatUpper}`;
    }

    return baseTitle;
  }

  get estimationDate() {
    const dates = this.detailData.estimationDate?.split(" - ") || [];
    if (dates.length < 2) return this.detailData.estimationDate || "-";

    const isMonthYearIsSame =
      formatDateNormal(dates[0], "MMMM YYYY") ===
      formatDateNormal(dates[1], "MMMM YYYY");

    const estimationDateStart = formatDateNormal(
      dates[0],
      `${isMonthYearIsSame ? "D" : "D MMMM YYYY"}`
    );
    const estimationDateEnd = formatDateNormal(dates[1], "D MMMM YYYY");

    return `${estimationDateStart} - ${estimationDateEnd}`;
  }

  get currencyPrefix(): string {
    return this.currencyCode === "MYR" ? "RM" : "Rp";
  }

  formatCompactPrice(value: number) {
    if (!value) return "0";

    if (value >= 1000000) {
      return `${this.currencyPrefix}${this.$t(
        "bookingV4.voucher.card.million",
        {
          value: Math.floor(value / 1000000)
        }
      )}`;
    }

    if (value >= 1000) {
      return `${this.currencyPrefix}${this.$t(
        "bookingV4.voucher.card.thousand",
        {
          value: Math.floor(value / 1000)
        }
      )}`;
    }

    return `${this.currencyPrefix}${value}`;
  }

  setBookingSuccessModal() {
    this.messageEntities = new ModalMessageEntities({
      customWidth: "520px",
      hideHeader: true,
      image: "badge-success",
      imageWidth: "24",
      imageheight: "24"
    });
  }

  onClose() {
    this.deactivateFocusTrap();
    this.$emit("close");

    if (this.showCreateInvoice) {
      PrintReceiptController.setVisibleToastHistorySuccessBooking(true);
    }
  }

  onSubmit() {
    this.deactivateFocusTrap();
    this.$emit("print", this.detailStt.sttId, this.detailStt.sttNo);
    this.$emit("close");
  }

  onCopyStt() {
    if (this.detailStt?.sttNo) {
      navigator.clipboard.writeText(this.detailStt.sttNo);
    }
  }

  formatPriceRP(price: any) {
    return formatPriceRP(price, this.currencyCode);
  }

  get formatHidePrice() {
    if (this.isCrossCountryCod) {
      return `${this.prefixCurrency}••••••`;
    }

    return this.currencyCode === "MYR" ? "RM••••••" : "Rp••••••";
  }

  formatPriceCurrency(value: number): string {
    if (this.isCrossCountryCod) {
      const hasFraction = (num: number) => {
        return typeof num === 'number' && !Number.isInteger(num);
      }
      const codAmount = formatByCountry(value, {
        type: "money",
        country: this.modelValue.receiver.destinationCountry.code,
        fractionDigits: hasFraction(value) ? 2 : 0,
        minFractionDigits: hasFraction(value) ? 2 : 0
      });

      return `${this.prefixCurrency}${codAmount}`;
    }

    return this.formatPriceRP(value);
  }

  get showShippingTariff(): boolean {
    if (!this.bookingClient) return true;
    if (AccountController.accountData.isAllClient) {
      return AccountController.accountData.account_type_detail
        .isShowShippingRate;
    }
    return this.selectedClient?.isShowShippingRate === true;
  }

  get isTariffMasked(): boolean {
    return this.bookingClient && !this.showShippingTariff;
  }

  get isPayableByReceiverMasked(): boolean {
    return this.isTariffMasked && !this.detailData.isCod;
  }

  get recipientName() {
    return ellipsisString(this.detailData.recipientName, 25);
  }

  /* Invoice Limit is not available on MY retail branch.
  get isLimitTagihan(): boolean {
    return this.detailData.sttPaymentMethod === PaymentMethodV4.INVOICE_LIMIT;
  }

  get paymentBadgeText(): string {
    return this.isLimitTagihan
      ? (this.$t("bookingV4.success.billingLimit") as string)
      : this.detailData.paymentMethodLabel || this.paymentMethodDisplay;
  }
  */

  get paymentBadgeClass(): string {
    // if (this.isLimitTagihan) {
    //   return "bg-purple-lp-100 text-purple-lp-600 font-medium";
    // }
    return "bg-gray-lp-1000 text-gray-lp-300 font-normal";
  }

  get paymentMethodDisplay() {
    return (
      PaymentController.paymentMethod.title ||
      (this.$t("bookingV4.payment.cash") as string)
    );
  }

  get paymentBadgeText(): string {
    const paymentMethod =
      this.detailData.paymentMethodLabel || this.paymentMethodDisplay;

    return ["cash", "tunai"].includes(paymentMethod.toLowerCase())
      ? (this.$t("bookingV4.payment.cash") as string)
      : paymentMethod;
  }

  get hasPromoDiscount() {
    return !!this.combinedPromoDiscountLabel;
  }

  /*
  get hasVoucherDiscount() {
    return (this.detailData.voucherDiscountAmount || 0) > 0;
  }
  */

  get showOriginalTariff() {
    if (this.detailData.isCod || this.detailData.isDfod) {
      return false;
    }
    return this.originalTariff > this.finalTariff;
  }

  get originalTariff() {
    return (
      this.detailStt.totalBeforeDiscount || this.detailStt.totalAfterDiscount
    );
  }

  get finalTariff() {
    if (this.detailData.isCod || this.detailData.isDfod) {
      return (
        this.detailStt.sttCodAmount ||
        this.detailData.codAmount ||
        this.detailStt.codAmount ||
        0
      );
    }
    return this.detailStt.totalAfterDiscount;
  }

  get combinedPromoDiscountLabel() {
    const discountList = this.detailData?.listDiscount || [];

    const promoLabels = discountList
      .filter((item: any) => {
        const category =
          item.config?.pdc_category ||
          item.config?.pdcCategory ||
          item.pdc_category ||
          item.pdcCategory;
        return ["tactical", "campaign"].includes(category);
      })
      .map((item: any) => {
        return (
          item.config?.pdc_promo_discount ??
          item.config?.pdcPromoDiscount ??
          item.pdc_promo_discount ??
          item.pdcPromoDiscount
        );
      })
      .filter((item: any) => item !== undefined && item !== null && item !== "")
      .map((item: any) => `${item}%`);

    if (!promoLabels.length) return "";
    return `Diskon ${promoLabels.join(" + ")}`;
  }

  deactivateFocusTrap() {
    if (this.focusTrap) {
      this.focusTrap = null;
    }
  }

  get showCreateInvoice() {
    const flag = FlagsPermissionBooking.flag_booking_print_invoice_receipt_enabled.isEnabled();
    const { isPosAccount } = AccountController.accountData;
    return flag && isPosAccount && this.isButtonCreateInvoice;
  }

  onPreviewInvoice() {
    this.onClose();
    this.$emit("previewInvoice", this.detailStt.sttId, this.detailStt.sttNo);
    PrintReceiptController.setVisiblePreviewInvoice(true);
    PrintReceiptController.setProcessInvoiceFromSuccessBooking(true);
  }
}
export default ModalSuccessBookingV4;
